/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { getNotificationPlatformOrigin, loadAuthorizedNotificationRoutes, resolveNotificationSourceLink, type AuthorizedPortalAction } from '../../utils/notification-source-link.utils';
import { CompanyScopeChangedError, getCompanyScopeSnapshot, registerCompanyScopeInvalidator } from '../../utils/company-scope-lifecycle.utils';
import { pickLocalizedAppLabel, useApps } from '../../context/AppsContext';
import { useTheme } from '../../context/useTheme';
import { useDateTime } from '../../hooks/useDateTime';
import { useTranslation } from '../../hooks/useTranslation';
import { withViteBasePathForApiDms } from '../../utils/api-same-origin.utils';
import { AppLogo, getRegistryCodeForSourceApp, normalizeSourceAppCode } from './AppLogo';
import { Icon } from './ComponentIcon';
import { HeaderHelpMenu, type HeaderHelpConfig } from './HeaderHelpMenu';
import {
  ProductTourHost,
  type ProductTourHostHandle,
  type ProductTourLaunchMode,
} from './ProductTourHost';

export type TopHeaderToolbarNotificationItem = {
  id: string;
  companyId?: string | null;
  companyName?: string | null;
  sourceApp?: string | null;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
};

export type TopHeaderToolbarNotificationSnapshot = {
  items: TopHeaderToolbarNotificationItem[];
  unreadCount: number;
  preferences: Record<string, boolean>;
};

type TopHeaderToolbarNotificationSummaryPayload = {
  items?: TopHeaderToolbarNotificationItem[];
  unreadCount?: number;
  preferences?: Record<string, boolean>;
};

export type TopHeaderToolbarNotificationProvider = {
  load: () => Promise<TopHeaderToolbarNotificationSnapshot>;
  /** Header ilk açılış özeti; tercihler gibi panel içi verileri çekmeden hafif çalışır. */
  loadHeader?: () => Promise<TopHeaderToolbarNotificationSnapshot>;
  /** Periyodik header yenilemesi için hafif sayaç çağrısı; yoksa load() geriye uyumluluk için kullanılır. */
  loadUnreadCount?: () => Promise<number>;
  markAsRead?: (item: TopHeaderToolbarNotificationItem) => Promise<void>;
};

export type TopHeaderToolbarNotificationEndpoints = {
  summaryPath?: string;
  listPath?: string;
  unreadCountPath?: string;
  preferencesPath?: string;
  markReadPath?: (notificationId: string) => string;
};

export type TopHeaderToolbarHelpConfig = HeaderHelpConfig;

export type TopHeaderToolbarAiAssistantContext = {
  pageContext?: Record<string, unknown>;
  userContext?: Record<string, unknown>;
  companyContext?: Record<string, unknown>;
  uiContext?: Record<string, unknown>;
};

export type TopHeaderToolbarAiAssistantConfig = {
  enabled?: boolean;
  /** Varsayılan: /api/assistant (ürün API'sinde server-token proxy) */
  apiBasePath?: string;
  /** Varsayılan: /ai/assistant/embed */
  embedPath?: string;
  applicationCode?: string;
  initialOpen?: boolean;
  contextProvider?: () => TopHeaderToolbarAiAssistantContext;
  labels?: {
    title?: string;
    button?: string;
    close?: string;
    unavailable?: string;
  };
};

export type TopHeaderToolbarNotificationLabels = {
  title?: string;
  unread?: string;
  typesMuted?: string;
  viewAll?: string;
  preferences?: string;
  mutedLabel?: string;
  empty?: string;
  /** delegationsPath verildiğinde alt bağlantı metni */
  delegations?: string;
};

export type TopHeaderToolbarNotificationCompany = {
  id: string | number;
  name?: string | null;
};

export type TopHeaderToolbarProps = {
  searchPath: string;
  notificationsPath: string;
  delegationsPath?: string;
  notificationsTenantReady: boolean;
  /**
   * Seçili firma (veya kiracı kapsamı) değişince bildirim paketi önbelleği sıfırlanır.
   * `notificationsTenantReady` titremesinde reset yapılmaz (API fırtınası önlemi).
   */
  notificationsBundleResetKey?: string;
  notificationTypeLabels?: Record<string, string>;
  notificationLabels?: TopHeaderToolbarNotificationLabels;
  notificationCompanies?: TopHeaderToolbarNotificationCompany[];
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  closeSearchAriaLabel?: string;
  pollIntervalMs?: number;
  maxNotificationItems?: number;
  notificationProvider?: TopHeaderToolbarNotificationProvider;
  notificationEndpoints?: TopHeaderToolbarNotificationEndpoints;
  locale?: string;
  helpConfig?: TopHeaderToolbarHelpConfig;
  aiAssistantConfig?: TopHeaderToolbarAiAssistantConfig;
  /** Header AI paneli açılırken (ör. `GlobalVoiceAssistant` kabuğu); verilirse embed iframe kabuğu bastırılır */
  onGlobalVoiceAssistantOpen?: () => void;
  /** `onGlobalVoiceAssistantOpen` ile: header oturumu kapanınca host asistanını kapat */
  onGlobalVoiceAssistantClose?: () => void;
  /** Sohbet paneli açık mı; `qp-gva-trigger` active senkronu */
  globalVoicePanelOpen?: boolean;
  /** Oturum açıkken panel küçültülmüşse robotta tekrar tıklanınca paneli aç */
  onGlobalVoiceAssistantExpandPanel?: () => void;
  /**
   * Sayfa yüklenince header AI tetikleyicisinin “açık” başlaması (ör. `sessionStorage` ile kalıcı panel).
   * Yalnızca `onGlobalVoiceAssistantOpen` verildiğinde (GlobalVoiceAssistant modu) anlamlıdır.
   */
  initialGlobalVoiceAssistantEngaged?: boolean;
};

const DEFAULT_NOTIFICATION_ENDPOINTS: Required<TopHeaderToolbarNotificationEndpoints> = {
  summaryPath: '/api/notifications/summary',
  listPath: '/api/notifications',
  unreadCountPath: '/api/notifications/unread-count',
  preferencesPath: '/api/notifications/preferences',
  markReadPath: (notificationId: string) => `/api/notifications/${notificationId}/read`,
};

/** Üst üste tamamlanan load() dalgalarını keser (dev/HMR/efekt yeniden kurulum) */
/** Kısa pencerede bildirim paketinin tekrar açılmasını kes */
const NOTIFICATION_BUNDLE_MIN_MS = 15000;
let notifBundleInflight: Promise<TopHeaderToolbarNotificationSnapshot> | null = null;
let notifLastSnapshot: TopHeaderToolbarNotificationSnapshot | null = null;
let notifLastBundleAt = 0;
let notifLastFailureAt = 0;
let notifHeaderInflight: Promise<TopHeaderToolbarNotificationSnapshot> | null = null;
let notifLastHeaderSnapshot: TopHeaderToolbarNotificationSnapshot | null = null;
let notifLastHeaderAt = 0;
let notifLastHeaderFailureAt = 0;
let notificationGeneration = 0;

const NOTIFICATION_POLL_LEADER_KEY = 'qubepro_notification_poll_leader';
const getScopedNotificationLeaderKey = () => `${NOTIFICATION_POLL_LEADER_KEY}:${getCompanyScopeSnapshot().key}`;
const NOTIFICATION_POLL_TAB_KEY = 'qubepro_notification_poll_tab';
const NOTIFICATION_POLL_DOCUMENT_GLOBAL_KEY = 'qubepro_notification_poll_document';
const NOTIFICATION_POLL_LEADER_TTL_MS = 9000;
const NOTIFICATION_POLL_LEADER_HEARTBEAT_MS = 3000;
const NOTIFICATION_BROADCAST_CHANNEL = 'qubepro-top-header-notifications';

type NotificationLeaderRecord = {
  id: string;
  at: number;
};

let notificationLeaderStorageBroken = false;

function createNotificationTabId(): string {
  return `nt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function getNotificationDocumentId(): string {
  const g = globalThis as typeof globalThis & Record<string, string | undefined>;
  if (!g[NOTIFICATION_POLL_DOCUMENT_GLOBAL_KEY]) {
    g[NOTIFICATION_POLL_DOCUMENT_GLOBAL_KEY] = createNotificationTabId();
  }
  return g[NOTIFICATION_POLL_DOCUMENT_GLOBAL_KEY]!;
}

function getNotificationTabId(): string {
  try {
    let current = sessionStorage.getItem(NOTIFICATION_POLL_TAB_KEY);
    if (!current) {
      current = createNotificationTabId();
      sessionStorage.setItem(NOTIFICATION_POLL_TAB_KEY, current);
    }
    // sessionStorage duplicate tab ile kopyalanabilir; document id fiziksel dokumani ayirir.
    return `${current}/${getNotificationDocumentId()}`;
  } catch {
    return `${createNotificationTabId()}/${getNotificationDocumentId()}`;
  }
}

function readNotificationLeader(key = getScopedNotificationLeaderKey()): NotificationLeaderRecord | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NotificationLeaderRecord>;
    if (!parsed.id || typeof parsed.at !== 'number') return null;
    return { id: parsed.id, at: parsed.at };
  } catch {
    notificationLeaderStorageBroken = true;
    return null;
  }
}

function writeNotificationLeader(id: string, key = getScopedNotificationLeaderKey()): boolean {
  try {
    const record = { id, at: Date.now() };
    localStorage.setItem(key, JSON.stringify(record));
    return readNotificationLeader(key)?.id === id;
  } catch {
    notificationLeaderStorageBroken = true;
    return false;
  }
}

function tryClaimNotificationLeader(id: string, key = getScopedNotificationLeaderKey()): boolean {
  if (notificationLeaderStorageBroken) return true;
  const current = readNotificationLeader(key);
  const expired = !current || Date.now() - current.at > NOTIFICATION_POLL_LEADER_TTL_MS;
  if (current?.id === id || expired) {
    return writeNotificationLeader(id, key) || notificationLeaderStorageBroken;
  }
  return false;
}

function isCurrentNotificationLeader(id: string, key = getScopedNotificationLeaderKey()): boolean {
  if (notificationLeaderStorageBroken) return true;
  return readNotificationLeader(key)?.id === id;
}

function releaseNotificationLeader(id: string, key = getScopedNotificationLeaderKey()): void {
  if (notificationLeaderStorageBroken) return;
  try {
    const current = readNotificationLeader(key);
    if (current?.id === id) {
      localStorage.removeItem(key);
    }
  } catch {
    notificationLeaderStorageBroken = true;
    /* localStorage yoksa yapılacak iş yok */
  }
}

export function resetTopHeaderNotificationBundleDedupeState(): void {
  notificationGeneration += 1;
  notifBundleInflight = null;
  notifLastSnapshot = null;
  notifLastBundleAt = 0;
  notifLastFailureAt = 0;
  notifHeaderInflight = null;
  notifLastHeaderSnapshot = null;
  notifLastHeaderAt = 0;
  notifLastHeaderFailureAt = 0;
}

registerCompanyScopeInvalidator(resetTopHeaderNotificationBundleDedupeState);

function cloneNotificationSnapshot(s: TopHeaderToolbarNotificationSnapshot): TopHeaderToolbarNotificationSnapshot {
  return {
    items: Array.isArray(s.items) ? s.items.map((i) => ({ ...i })) : [],
    unreadCount: Number(s.unreadCount || 0),
    preferences: { ...(s.preferences || {}) },
  };
}

async function dedupeNotificationBundle(
  fetcher: () => Promise<TopHeaderToolbarNotificationSnapshot>,
): Promise<TopHeaderToolbarNotificationSnapshot> {
  if (notifBundleInflight) {
    return notifBundleInflight;
  }
  const now = Date.now();
  if (notifLastSnapshot && now - notifLastBundleAt < NOTIFICATION_BUNDLE_MIN_MS) {
    return Promise.resolve(cloneNotificationSnapshot(notifLastSnapshot));
  }
  if (notifLastFailureAt > 0 && now - notifLastFailureAt < NOTIFICATION_BUNDLE_MIN_MS) {
    return Promise.resolve(
      notifLastSnapshot
        ? cloneNotificationSnapshot(notifLastSnapshot)
        : { items: [], unreadCount: 0, preferences: {} },
    );
  }
  const generation = notificationGeneration;
  notifBundleInflight = fetcher()
    .then((snap) => {
      if (generation !== notificationGeneration) throw new CompanyScopeChangedError();
      notifLastSnapshot = snap;
      notifLastBundleAt = Date.now();
      notifLastFailureAt = 0;
      return snap;
    })
    .catch((err) => {
      if (generation === notificationGeneration) notifLastFailureAt = Date.now();
      throw err;
    })
    .finally(() => {
      if (generation === notificationGeneration) notifBundleInflight = null;
    });
  return notifBundleInflight;
}

async function dedupeNotificationHeaderBundle(
  fetcher: () => Promise<TopHeaderToolbarNotificationSnapshot>,
): Promise<TopHeaderToolbarNotificationSnapshot> {
  if (notifHeaderInflight) {
    return notifHeaderInflight;
  }
  const now = Date.now();
  if (notifLastHeaderSnapshot && now - notifLastHeaderAt < NOTIFICATION_BUNDLE_MIN_MS) {
    return Promise.resolve(cloneNotificationSnapshot(notifLastHeaderSnapshot));
  }
  if (notifLastHeaderFailureAt > 0 && now - notifLastHeaderFailureAt < NOTIFICATION_BUNDLE_MIN_MS) {
    return Promise.resolve(
      notifLastHeaderSnapshot
        ? cloneNotificationSnapshot(notifLastHeaderSnapshot)
        : { items: [], unreadCount: 0, preferences: {} },
    );
  }
  const generation = notificationGeneration;
  notifHeaderInflight = fetcher()
    .then((snap) => {
      if (generation !== notificationGeneration) throw new CompanyScopeChangedError();
      notifLastHeaderSnapshot = snap;
      notifLastHeaderAt = Date.now();
      notifLastHeaderFailureAt = 0;
      return snap;
    })
    .catch((err) => {
      if (generation === notificationGeneration) notifLastHeaderFailureAt = Date.now();
      throw err;
    })
    .finally(() => {
      if (generation === notificationGeneration) notifHeaderInflight = null;
    });
  return notifHeaderInflight;
}

const TOOLBAR_CSS = `
.qp-top-header-toolbar-search {
  display: inline-flex;
  align-items: center;
  flex: 1 1 clamp(44px, 16vw, 260px);
  gap: 6px;
  position: relative;
  min-width: 44px;
  max-width: 280px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  overflow: hidden;
}

.qp-top-header-toolbar-search-trigger,
.qp-top-header-toolbar-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  box-shadow: none;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.qp-top-header-toolbar-search-trigger:hover,
.qp-top-header-toolbar-search-clear:hover {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
  color: var(--text-primary);
}

.qp-top-header-toolbar-search-trigger:focus-visible,
.qp-top-header-toolbar-search-clear:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

.qp-top-header-toolbar-search-clear {
  display: none;
}

.qp-top-header-toolbar-search .icon {
  flex-shrink: 0;
  color: currentColor;
  transition: color 0.2s ease;
}

.qp-top-header-toolbar-search:focus-within .qp-top-header-toolbar-search-trigger {
  color: var(--text-primary);
}

.qp-top-header-toolbar-search input {
  appearance: none;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.35;
  flex: 1 1 auto;
  width: clamp(2rem, 12vw, 14rem);
  min-width: 0;
  max-width: 14rem;
  padding: 6px 0;
}

.qp-top-header-toolbar-search input::placeholder {
  color: var(--text-tertiary);
}

.qp-top-header-toolbar-search input:focus-visible {
  outline: none;
}

.qp-top-header-toolbar-search:has(input:focus-visible) {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
  border-radius: 8px;
}

.qp-top-header-toolbar-notification-wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.qp-top-header-toolbar-notification-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  box-shadow: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.qp-top-header-toolbar-notification-btn:hover {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
  color: var(--text-primary);
}

.qp-top-header-toolbar-notification-btn:active {
  background: color-mix(in srgb, var(--text-primary) 12%, transparent);
}

.qp-top-header-toolbar-notification-btn:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

.qp-top-header-toolbar-notification-btn .icon {
  color: inherit;
}

.qp-top-header-toolbar-notification-badge {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
  color: var(--bg-primary);
  background: var(--text-primary);
  box-sizing: border-box;
  border: 2px solid var(--bg-primary);
  pointer-events: none;
}

@keyframes qpNotifDropdownIn {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.qp-top-header-toolbar-notification-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1002;
  width: min(380px, calc(100vw - 32px));
  max-height: min(440px, 72vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--card-border, var(--border-color, #edebe9));
  background: var(--card-bg, var(--bg-primary, #ffffff));
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  color: var(--text-primary);
  animation: qpNotifDropdownIn 0.2s ease-out;
}

[data-theme="dark"] .qp-top-header-toolbar-notification-dropdown {
  background: var(--card-bg, #2d2c2c);
  border-color: var(--card-border, var(--border-color, #3b3a39));
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.qp-top-header-toolbar-notification-dropdown-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.qp-top-header-toolbar-notification-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.qp-top-header-toolbar-notification-subtitle {
  margin-top: 3px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.qp-top-header-toolbar-notification-head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  padding-top: 1px;
}

.qp-top-header-toolbar-notification-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 5px 10px;
  min-height: 28px;
  box-sizing: border-box;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--text-primary);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 0 color-mix(in srgb, var(--text-primary) 6%, transparent);
}

.qp-top-header-toolbar-notification-link:hover {
  background: color-mix(in srgb, var(--text-primary) 7%, var(--bg-secondary));
  border-color: color-mix(in srgb, var(--text-primary) 18%, var(--border-color));
  color: var(--text-primary);
}

.qp-top-header-toolbar-notification-link:active {
  box-shadow: none;
}

.qp-top-header-toolbar-notification-link:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: 2px;
}

[data-theme="dark"] .qp-top-header-toolbar-notification-link {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary, var(--border-color));
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
}

[data-theme="dark"] .qp-top-header-toolbar-notification-link:hover {
  background: color-mix(in srgb, var(--text-primary) 10%, var(--bg-tertiary));
  border-color: color-mix(in srgb, var(--text-primary) 22%, var(--border-color));
}

.qp-top-header-toolbar-notification-muted-summary {
  padding: 8px 16px;
  font-size: 12px;
  color: var(--text-tertiary);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.qp-top-header-toolbar-notification-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0;
}

.qp-top-header-toolbar-notification-list::-webkit-scrollbar {
  width: 6px;
}

.qp-top-header-toolbar-notification-list::-webkit-scrollbar-track {
  background: transparent;
}

.qp-top-header-toolbar-notification-list::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb, #d0d0d0);
  border-radius: 3px;
}

.qp-top-header-toolbar-notification-list::-webkit-scrollbar-thumb:hover {
  background: var(--scrollbar-thumb-hover, #b0b0b0);
}

.qp-top-header-toolbar-notification-empty {
  padding: 40px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.qp-top-header-toolbar-notification-empty::before {
  content: '';
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-tertiary, #f3f2f1);
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'/%3E%3Cpath d='M13.73 21a2 2 0 0 1-3.46 0'/%3E%3C/svg%3E");
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'/%3E%3Cpath d='M13.73 21a2 2 0 0 1-3.46 0'/%3E%3C/svg%3E");
}

.qp-top-header-toolbar-notification-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.qp-top-header-toolbar-notification-item-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.qp-top-header-toolbar-notification-app-logo {
  margin-top: 1px;
}

.qp-top-header-toolbar-notification-item-content {
  flex: 1;
  min-width: 0;
}

.qp-top-header-toolbar-notification-item:last-child {
  border-bottom: none;
}

.qp-top-header-toolbar-notification-item:hover {
  background: var(--bg-tertiary, #f3f2f1);
}

/* Okunmamış: nötr başlangıç çizgisi; tam satır boyası yok */
.qp-top-header-toolbar-notification-item.unread {
  border-inline-start: 3px solid var(--text-primary);
}

.qp-top-header-toolbar-notification-item-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}

.qp-top-header-toolbar-notification-item-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.qp-top-header-toolbar-notification-company-badge {
  display: inline-block;
  max-width: min(180px, 100%);
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.qp-top-header-toolbar-notification-type-badge {
  display: inline-block;
  max-width: min(160px, 100%);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background: var(--bg-tertiary);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-tertiary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.qp-top-header-toolbar-notification-item.unread .qp-top-header-toolbar-notification-type-badge {
  color: var(--text-secondary);
  border-color: color-mix(in srgb, var(--text-primary) 14%, var(--border-color));
}

.qp-top-header-toolbar-notification-muted-badge {
  font-size: 9px;
  color: var(--text-tertiary);
  font-style: italic;
}

.qp-top-header-toolbar-notification-item-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.35;
}

.qp-top-header-toolbar-notification-item-message {
  margin-top: 3px;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.qp-top-header-toolbar-notification-item-time {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--text-tertiary);
  line-height: 1.35;
  white-space: nowrap;
}

.qp-top-header-toolbar-notification-footer {
  padding: 10px 16px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
}

[dir="rtl"] .qp-top-header-toolbar-notification-dropdown {
  right: auto;
  left: 0;
}

[dir="rtl"] .qp-top-header-toolbar-notification-badge {
  right: auto;
  left: 3px;
}

[dir="rtl"] .qp-top-header-toolbar-notification-item {
  text-align: right;
}

.qp-gva-container {
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  /* Sıra DOM'dan gelir: zil · Yardım · AI · (parent) ▦ · avatar. order override kullanılmaz. */
}

.qp-gva-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  box-shadow: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* İkon ışıltının üstünde kalsın. */
.qp-gva-trigger > svg,
.qp-gva-trigger > .icon {
  position: relative;
  z-index: 1;
}

/* AI ışıltısı: yumuşak menekşe→camgöbeği radyal hale; normalde gizli. */
.qp-gva-trigger::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 50%,
    rgba(139, 92, 246, 0.55),
    rgba(56, 189, 248, 0.35) 48%,
    transparent 72%);
  opacity: 0;
  transform: scale(0.55);
  filter: blur(5px);
  transition: opacity 0.35s ease, transform 0.35s ease;
  pointer-events: none;
  z-index: 0;
}

.qp-gva-trigger:hover {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
  color: var(--text-primary);
}
.qp-gva-trigger:hover::before {
  opacity: 0.85;
  transform: scale(1);
}

/* Seçili (panel açık): accent renk + kalıcı, nazikçe nabız atan ışıltı. */
.qp-gva-trigger.active {
  background: transparent;
  color: var(--accent-primary, #6366f1);
}
.qp-gva-trigger.active::before {
  opacity: 0.8;
  transform: scale(1);
  animation: qp-gva-glow-pulse 2.6s ease-in-out infinite;
}
@keyframes qp-gva-glow-pulse {
  0%, 100% { opacity: 0.5; transform: scale(0.9); }
  50% { opacity: 0.9; transform: scale(1.08); }
}
@media (prefers-reduced-motion: reduce) {
  .qp-gva-trigger.active::before {
    animation: none;
    opacity: 0.7;
    transform: scale(1);
  }
}

.qp-gva-trigger:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

/* AI tetik ikonu: hover'da yumuşak tam tur dönme + hafif büyüme (Gemini-vari, CSS-only). */
/* AI tetik ikonu: hover'da TEK tam tur donme + hafif buyume.
   NOT: Icon bileseni inline transition:color bastigi icin transform gecisi ezilir;
   bu yuzden donme animation ile yapilir (inline transition'dan etkilenmez). */
@keyframes qp-gva-spin {
  from { transform: rotate(0deg) scale(1); }
  to { transform: rotate(360deg) scale(1.06); }
}
.qp-gva-trigger:hover .icon,
.qp-gva-trigger:hover svg {
  animation: qp-gva-spin 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@media (prefers-reduced-motion: reduce) {
  .qp-gva-trigger:hover .icon,
  .qp-gva-trigger:hover svg {
    animation: none;
    transform: scale(1.06);
  }
}

/* Sağa yaslı, tam-boy yan panel (Gmail/Gemini deseni). Açılınca genişlikten kayar. */
.qp-gva-panel {
  position: fixed;
  top: 64px;
  right: 12px;
  bottom: 12px;
  z-index: 1003;
  width: 0;
  max-width: calc(100vw - 24px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
  border-radius: 14px;
  background: var(--bg-primary);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  color: var(--text-primary);
  opacity: 0;
  pointer-events: none;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease;
}

.qp-gva-panel.open {
  width: min(440px, calc(100vw - 24px));
  opacity: 1;
  pointer-events: auto;
}

/* "Genişlet" (Gmail/Gemini) — daha geniş çalışma alanı */
.qp-gva-panel.open.expanded {
  width: min(760px, calc(100vw - 24px));
}

[data-theme="dark"] .qp-gva-panel {
  background: transparent;
  box-shadow: none;
}

.qp-gva-frame {
  flex: 1;
  min-height: 0;
  width: 100%;
  border: 0;
  background: var(--bg-primary);
}

[dir="rtl"] .qp-top-header-toolbar-help-dropdown {
  right: auto;
  left: 0;
}

[dir="rtl"] .qp-top-header-toolbar-help-item {
  text-align: right;
}

@media (max-width: 768px) {
  .qp-top-header-toolbar-notification-wrap {
    margin-inline-start: 0;
  }

  .qp-top-header-toolbar-notification-dropdown {
    position: fixed;
    top: 72px;
    left: 16px;
    right: 16px;
    width: auto;
  }

  .qp-top-header-toolbar-search {
    flex: 0 0 36px;
    box-sizing: border-box;
    width: 36px;
    min-width: 36px;
    max-width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    transition:
      width 0.2s ease,
      max-width 0.2s ease,
      background-color 0.18s ease,
      border-color 0.18s ease,
      box-shadow 0.18s ease;
  }

  [data-theme="dark"] .qp-top-header-toolbar-search {
    background: transparent;
    border-color: transparent;
  }

  .qp-top-header-toolbar-search input {
    display: none;
  }

  .qp-top-header-toolbar-search-trigger {
    width: 36px;
    height: 36px;
  }

  .qp-top-header-toolbar-search-clear {
    width: 28px;
    height: 28px;
  }

  .qp-top-header-toolbar-search.is-mobile-expanded {
    position: fixed;
    z-index: 1004;
    inset-block-start: 10px;
    inset-inline-start: 68px;
    inset-inline-end: 12px;
    flex: 0 0 auto;
    width: auto;
    min-width: 0;
    max-width: none;
    height: 40px;
    justify-content: flex-start;
    gap: 8px;
    padding: 0 10px;
    border: 1px solid var(--border-color, #edebe9);
    border-radius: 999px;
    background: var(--bg-secondary, #faf9f8);
    cursor: text;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--text-primary) 10%, transparent);
  }

  [data-theme="dark"] .qp-top-header-toolbar-search.is-mobile-expanded {
    background: var(--bg-tertiary, #252423);
    border-color: var(--border-color, #3b3a39);
  }

  .qp-top-header-toolbar-search.is-mobile-expanded input {
    display: block;
    min-width: 0;
    width: 100%;
    max-width: none;
    flex: 1;
    padding: 4px 0;
  }

  .qp-top-header-toolbar-search.is-mobile-expanded .qp-top-header-toolbar-search-trigger {
    width: 28px;
    height: 28px;
  }

  .qp-top-header-toolbar-search.is-mobile-expanded .qp-top-header-toolbar-search-clear {
    display: inline-flex;
    margin-inline-start: auto;
  }

  .qp-top-header-toolbar-help-dropdown {
    position: fixed;
    top: 72px;
    left: 16px;
    right: 16px;
    width: auto;
  }

  /* Mobil: sağ panel tam ekran (genişlet anlamsız, max genişlik). */
  .qp-gva-panel {
    position: fixed;
    top: 56px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: 0;
    max-width: none;
  }

  .qp-gva-panel.open,
  .qp-gva-panel.open.expanded {
    width: auto;
    left: 8px;
    right: 8px;
  }
}

@media (max-width: 480px) {
  .qp-top-header-toolbar-search.is-mobile-expanded {
    inset-inline-start: 64px;
  }
}
`;

function extractArrayData<T>(payload: unknown): T[] {
  if (!payload || typeof payload !== 'object') return [];
  const body = payload as { data?: unknown };
  const inner = body.data ?? payload;
  if (Array.isArray(inner)) return inner as T[];
  if (inner && typeof inner === 'object' && Array.isArray((inner as { data?: unknown }).data)) {
    return (inner as { data: T[] }).data;
  }
  return [];
}

function extractObjectData<T extends object>(payload: unknown): T {
  if (!payload || typeof payload !== 'object') return {} as T;
  const body = payload as { data?: unknown };
  const inner = body.data ?? payload;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as T;
  }
  return {} as T;
}

function buildAiRequestUrl(apiBasePath: string, path: string) {
  const base = apiBasePath.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${suffix}`;
  return new URL(withViteBasePathForApiDms(url), window.location.origin).toString();
}

function getDefaultApplicationCode() {
  const env = import.meta.env as unknown as { VITE_APPLICATION_CODE?: string; VITE_APP_CODE?: string };
  return (env.VITE_APPLICATION_CODE || env.VITE_APP_CODE || '').trim() || 'CORE';
}

/**
 * Merkezi Bildirim ürününün (qubepro-notification) bildirim merkezi:
 * `{platformBase}/notification/notifications`.
 * App switcher (AppsMenuButton) ile aynı platform base çözümü; header'dan "Tüm bildirimler" /
 * "Tercihler" bu adrese YENİ SEKMEDE yönlenir (ürün ayrı bir uygulamadır).
 * tab='preferences' → ayrı bildirim tercihleri sayfası.
 */
function getNotificationCenterUrl(tab?: 'preferences'): string {
  const env =
    (import.meta.env.VITE_PLATFORM_URL as string) ||
    (import.meta.env.VITE_BASE_URL as string) ||
    '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const base = (env ? env.replace(/\/+$/, '') : origin);
  const notificationPath = tab === 'preferences'
    ? '/notification/notification-preferences'
    : '/notification/notifications';
  const url = new URL(`${base}${notificationPath}`, origin);
  return url.toString();
}

function buildAiEmbedUrl(embedPath: string, applicationCode: string) {
  const url = new URL(embedPath, window.location.origin);
  url.searchParams.set('applicationCode', applicationCode);
  url.searchParams.set('embedded', '1');
  return url.toString();
}

function createDefaultNotificationProvider(
  endpoints: Required<TopHeaderToolbarNotificationEndpoints>,
  maxNotificationItems: number,
): TopHeaderToolbarNotificationProvider {
  const readSummarySnapshot = (payload: unknown): TopHeaderToolbarNotificationSnapshot => {
    const data = extractObjectData<TopHeaderToolbarNotificationSummaryPayload>(payload);
    return {
      items: Array.isArray(data.items)
        ? data.items
        : extractArrayData<TopHeaderToolbarNotificationItem>(payload),
      unreadCount: Number(data.unreadCount || 0),
      preferences: data.preferences && typeof data.preferences === 'object'
        ? { ...data.preferences }
        : {},
    };
  };

  const httpStatusOf = (error: unknown): number | undefined => {
    const maybe = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
    const raw = maybe?.response?.status ?? maybe?.status;
    const status = Number(raw);
    return Number.isFinite(status) ? status : undefined;
  };

  const loadLegacyHeader = async (): Promise<TopHeaderToolbarNotificationSnapshot> => {
    const [listRes, unreadRes] = await Promise.all([
      axios.get<unknown>(endpoints.listPath, {
        params: { page: 1, size: maxNotificationItems },
      }),
      axios.get<unknown>(endpoints.unreadCountPath),
    ]);
    const unreadData = extractObjectData<{ unreadCount?: number }>(unreadRes.data);

    return {
      items: extractArrayData<TopHeaderToolbarNotificationItem>(listRes.data),
      unreadCount: Number(unreadData.unreadCount || 0),
      preferences: {},
    };
  };

  const loadLegacyBundle = async (): Promise<TopHeaderToolbarNotificationSnapshot> => {
    const [listRes, unreadRes, preferencesRes] = await Promise.all([
      axios.get<unknown>(endpoints.listPath, {
        params: { page: 1, size: maxNotificationItems },
      }),
      axios.get<unknown>(endpoints.unreadCountPath),
      axios.get<unknown>(endpoints.preferencesPath),
    ]);
    const unreadData = extractObjectData<{ unreadCount?: number }>(unreadRes.data);
    const preferences = extractObjectData<Record<string, boolean>>(preferencesRes.data);

    return {
      items: extractArrayData<TopHeaderToolbarNotificationItem>(listRes.data),
      unreadCount: Number(unreadData.unreadCount || 0),
      preferences: { ...preferences },
    };
  };

  const loadSummary = async (includePreferences: boolean): Promise<TopHeaderToolbarNotificationSnapshot> => {
    try {
      const summaryRes = await axios.get<unknown>(endpoints.summaryPath, {
        params: {
          size: maxNotificationItems,
          ...(includePreferences ? { includePreferences: '1' } : {}),
        },
      });
      return readSummarySnapshot(summaryRes.data);
    } catch (error) {
      if (httpStatusOf(error) === 404) {
        return includePreferences ? loadLegacyBundle() : loadLegacyHeader();
      }
      throw error;
    }
  };

  return {
    loadHeader: () => loadSummary(false),
    load: () => loadSummary(true),
    loadUnreadCount: async () => {
      const unreadRes = await axios.get<unknown>(endpoints.unreadCountPath);
      const unreadData = extractObjectData<{ unreadCount?: number }>(unreadRes.data);
      return Number(unreadData.unreadCount || 0);
    },
    markAsRead: async (item) => {
      await axios.post(endpoints.markReadPath(item.id), {});
    },
  };
}

export function TopHeaderToolbar({
  searchPath,
  delegationsPath,
  notificationsTenantReady,
  notificationsBundleResetKey,
  notificationTypeLabels = {},
  notificationLabels,
  notificationCompanies = [],
  searchPlaceholder = 'Search',
  searchAriaLabel = 'Search',
  closeSearchAriaLabel = 'Close search',
  pollIntervalMs = 30000,
  maxNotificationItems = 6,
  notificationProvider,
  notificationEndpoints,
  locale = 'tr-TR',
  helpConfig,
  aiAssistantConfig,
  onGlobalVoiceAssistantOpen,
  onGlobalVoiceAssistantClose,
  globalVoicePanelOpen,
  onGlobalVoiceAssistantExpandPanel,
  initialGlobalVoiceAssistantEngaged = false,
}: TopHeaderToolbarProps) {
  const notificationScope = useSyncExternalStore(registerCompanyScopeInvalidator, () => getCompanyScopeSnapshot().key, () => '');
  const navigate = useNavigate();
  const location = useLocation();
  const dateTime = useDateTime();
  const t = useTranslation();
  const { apps, getApp, getIconColor } = useApps();
  const { user } = useAuth();
  const [sourceActions, setSourceActions] = useState<AuthorizedPortalAction[]>([]);
  useEffect(() => {
    let active = true;
    setSourceActions([]);
    if (user && notificationsTenantReady) {
      void loadAuthorizedNotificationRoutes().then((actions) => {
        if (active) setSourceActions(actions);
      }).catch(() => { if (active) setSourceActions([]); });
    }
    return () => { active = false; };
  }, [user?.id, notificationsBundleResetKey, notificationsTenantReady]);
  const sourceLinkFor = useCallback((item: TopHeaderToolbarNotificationItem, actions = sourceActions) =>
    resolveNotificationSourceLink(item, actions, apps, user?.permissions ?? [], Boolean(user?.isAdmin), getNotificationPlatformOrigin()),
    [apps, sourceActions, user?.isAdmin, user?.permissions]);
  const { theme } = useTheme();
  const [headerSearch, setHeaderSearch] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [productTourLaunchMode, setProductTourLaunchMode] = useState<ProductTourLaunchMode | null>('restart');
  const [notificationItems, setNotificationItems] = useState<TopHeaderToolbarNotificationItem[]>([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationPreferences, setNotificationPreferences] = useState<Record<string, boolean>>({});
  const [aiOpen, setAiOpen] = useState(
    () =>
      Boolean(aiAssistantConfig?.initialOpen) ||
      (Boolean(onGlobalVoiceAssistantOpen) && Boolean(initialGlobalVoiceAssistantEngaged)),
  );
  const [aiAvailable, setAiAvailable] = useState(true);
  /** Sağ panel genişletilmiş mi (Gmail/Gemini "genişlet"); iframe'den postMessage ile gelir. */
  const [aiExpanded, setAiExpanded] = useState(false);
  const suppressAiEmbedPanel = Boolean(onGlobalVoiceAssistantOpen);
  const robotTriggerActive =
    suppressAiEmbedPanel && typeof globalVoicePanelOpen === 'boolean'
      ? Boolean(aiOpen && globalVoicePanelOpen)
      : aiOpen;
  const prevAiOpenRef = useRef<boolean | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const aiRef = useRef<HTMLDivElement>(null);
  const aiFrameRef = useRef<HTMLIFrameElement>(null);
  const searchRef = useRef<HTMLFormElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const productTourRef = useRef<ProductTourHostHandle>(null);
  const notificationItemsRef = useRef<TopHeaderToolbarNotificationItem[]>(notificationItems);
  const notificationPreferencesRef = useRef<Record<string, boolean>>(notificationPreferences);
  const notificationTabIdRef = useRef(getNotificationTabId());
  const notificationBroadcastRef = useRef<BroadcastChannel | null>(null);
  const [notificationPollLeader, setNotificationPollLeader] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );

  useEffect(() => {
    notificationItemsRef.current = notificationItems;
  }, [notificationItems]);

  useEffect(() => {
    notificationPreferencesRef.current = notificationPreferences;
  }, [notificationPreferences]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(`${NOTIFICATION_BROADCAST_CHANNEL}:${notificationScope}`);
    notificationBroadcastRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      const msg = event.data as { type?: string; source?: string; snapshot?: TopHeaderToolbarNotificationSnapshot };
      if (msg?.type !== 'snapshot' || msg.source === notificationTabIdRef.current || !msg.snapshot) return;
      setNotificationItems(Array.isArray(msg.snapshot.items) ? msg.snapshot.items : []);
      setNotificationUnreadCount(Number(msg.snapshot.unreadCount || 0));
      setNotificationPreferences({ ...(msg.snapshot.preferences || {}) });
    };

    return () => {
      if (notificationBroadcastRef.current === channel) {
        notificationBroadcastRef.current = null;
      }
      channel.close();
    };
  }, [notificationScope]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setPageVisible(document.visibilityState !== 'hidden');
    };
    onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      if (notificationRef.current && !notificationRef.current.contains(target)) {
        setNotificationOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(target) && !headerSearch.trim()) {
        setSearchExpanded(false);
      }
      if (aiRef.current && !aiRef.current.contains(target)) {
        if (suppressAiEmbedPanel) {
          // Host GlobalVoiceAssistant gerçek split-layout panelidir; ana içerikte çalışma paneli kapatmamalı.
          return;
        } else {
          const w = aiFrameRef.current?.contentWindow;
          if (w) w.postMessage({ type: 'qubepro-ai-close' }, window.location.origin);
          setAiOpen(false);
        }
      }
    },
    [headerSearch, suppressAiEmbedPanel],
  );

  useEffect(() => {
    if (!notificationOpen && !helpOpen && !aiOpen && !searchExpanded) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationOpen, helpOpen, aiOpen, searchExpanded, handleClickOutside]);

  useEffect(() => {
    if (prevAiOpenRef.current === true && !aiOpen && onGlobalVoiceAssistantOpen) {
      onGlobalVoiceAssistantClose?.();
    }
    prevAiOpenRef.current = aiOpen;
  }, [aiOpen, onGlobalVoiceAssistantOpen, onGlobalVoiceAssistantClose]);

  const resolvedEndpoints = useMemo(
    () => ({
      ...DEFAULT_NOTIFICATION_ENDPOINTS,
      ...notificationEndpoints,
    }),
    [notificationEndpoints],
  );

  const resolvedProvider = useMemo(
    () => notificationProvider || createDefaultNotificationProvider(resolvedEndpoints, maxNotificationItems),
    [maxNotificationItems, notificationProvider, resolvedEndpoints],
  );

  // Üst bileşen her render'da yeni `notificationProvider` verince kimlik dalgalanır; efekt bağımlılığında
  // olursa interval sürekli yeniden kurulur ve bildirim API'leri fırtına yapar. Güncel yükleyici ref üzerinden.
  const resolvedProviderRef = useRef(resolvedProvider);
  resolvedProviderRef.current = resolvedProvider;

  /** Kiracı hazır bayrağı kısa false olsa bile polling'i sökme (420ms uydu dalgasında çok kısaydı) */
  const NOTIFICATION_TENANT_GATE_OFF_MS = 1600;
  const [notificationsPollGate, setNotificationsPollGate] = useState(notificationsTenantReady);
  useEffect(() => {
    if (notificationsTenantReady) {
      setNotificationsPollGate(true);
      return;
    }
    const t = window.setTimeout(() => setNotificationsPollGate(false), NOTIFICATION_TENANT_GATE_OFF_MS);
    return () => window.clearTimeout(t);
  }, [notificationsTenantReady]);

  useEffect(() => {
    const tabId = notificationTabIdRef.current;
    const leaderKey = `${NOTIFICATION_POLL_LEADER_KEY}:${notificationScope}`;
    if (!notificationsPollGate || !pageVisible) {
      releaseNotificationLeader(tabId, leaderKey);
      setNotificationPollLeader(false);
      return;
    }

    const refreshLeadership = () => {
      setNotificationPollLeader(tryClaimNotificationLeader(tabId, leaderKey));
    };

    refreshLeadership();
    const timer = window.setInterval(refreshLeadership, NOTIFICATION_POLL_LEADER_HEARTBEAT_MS);
    const onStorage = (event: StorageEvent) => {
      if (event.key === leaderKey) refreshLeadership();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', onStorage);
      releaseNotificationLeader(tabId, leaderKey);
      setNotificationPollLeader(false);
    };
  }, [notificationsPollGate, pageVisible, notificationScope]);

  const languagePrefix = useMemo(() => {
    const match = location.pathname.match(/^\/(tr|en)(\/|$)/);
    if (match?.[1]) return `/${match[1]}`;

    const htmlLang = document.documentElement.lang || '';
    if (htmlLang.toLowerCase().startsWith('en')) return '/en';
    if (htmlLang.toLowerCase().startsWith('tr')) return '/tr';

    const normalizedLocale = (locale || '').toLowerCase();
    if (normalizedLocale.startsWith('en')) return '/en';
    if (normalizedLocale.startsWith('tr')) return '/tr';

    const navLang = (navigator.language || '').toLowerCase();
    return navLang.startsWith('en') ? '/en' : '/tr';
  }, [locale, location.pathname]);

  const withLanguagePrefix = useCallback(
    (rawUrl: string) => {
      if (!rawUrl) return rawUrl;

      const url = new URL(rawUrl, window.location.origin);
      const pathname = url.pathname || '/';

      if (/^\/(tr|en)(\/|$)/.test(pathname)) {
        return url.toString();
      }

      const normalizedPrefix = languagePrefix.endsWith('/') ? languagePrefix.slice(0, -1) : languagePrefix;
      const docsLanguage = normalizedPrefix.replace(/^\//, '') || 'tr';
      const docsHost = String(import.meta.env.VITE_DOCS_HOST || '')
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/.*$/, '');
      if (docsHost && url.hostname.toLowerCase() === docsHost.toLowerCase()) {
        const normalizedDocsPath = pathname
          .replace(/^\/(tr|en)(\/|$)/i, '/')
          .replace(/^\/\+[a-z]{2}\+(\/|$)/i, '/')
          .replace(/^\/+/, '/');
        url.protocol = 'https:';
        url.hostname = docsHost;
        url.pathname = `/${docsLanguage}${normalizedDocsPath === '/' ? '' : normalizedDocsPath}`;
        return url.toString();
      }

      const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
      url.pathname = `${normalizedPrefix}${normalizedPath}`;

      return url.toString();
    },
    [languagePrefix],
  );

  const mutedNotificationTypes = useMemo(
    () =>
      Object.entries(notificationPreferences)
        .filter(([, enabled]) => enabled === false)
        .map(([type]) => notificationTypeLabels[type] || type),
    [notificationPreferences, notificationTypeLabels],
  );
  const aiEnabled = Boolean(aiAssistantConfig) && aiAssistantConfig?.enabled !== false;
  const aiApiBasePath = aiAssistantConfig?.apiBasePath || '/api/assistant';
  const aiEmbedPath = aiAssistantConfig?.embedPath || '/ai/assistant/embed';
  const aiLabels = aiAssistantConfig?.labels || {};
  const aiApplicationCode = aiAssistantConfig?.applicationCode || getDefaultApplicationCode();
  const aiEmbedUrl = useMemo(
    () => buildAiEmbedUrl(aiEmbedPath, aiApplicationCode),
    [aiApplicationCode, aiEmbedPath],
  );

  const postAiCommand = useCallback((type: string) => {
    const frameWindow = aiFrameRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.postMessage({ type }, window.location.origin);
  }, []);

  const prevHeaderPathRef = useRef<string | null>(null);
  useEffect(() => {
    // İlk mount'ta (F5) AI panelini kapatma — pozisyon yerel depodan geri yüklendiyse korunsun.
    // Yalnızca GERÇEK sayfa (route) değişiminde temizle. Pathname karşılaştırması StrictMode'da da
    // (dev çift-invoke) doğru çalışır; boolean ref güvenli değildir.
    if (prevHeaderPathRef.current === null || prevHeaderPathRef.current === location.pathname) {
      prevHeaderPathRef.current = location.pathname;
      return;
    }
    prevHeaderPathRef.current = location.pathname;
    setHeaderSearch('');
    setSearchExpanded(false);
    setNotificationOpen(false);
    setHelpOpen(false);
    if (!suppressAiEmbedPanel) {
      setAiOpen(false);
      postAiCommand('qubepro-ai-close');
    }
  }, [location.pathname, postAiCommand, suppressAiEmbedPanel]);

  const openMobileSearch = useCallback(() => {
    const isCompact = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    if (!isCompact) return false;
    if (!searchExpanded) {
      setSearchExpanded(true);
      setNotificationOpen(false);
      setHelpOpen(false);
      if (!suppressAiEmbedPanel) {
        postAiCommand('qubepro-ai-close');
        setAiOpen(false);
      }
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return true;
    }
    return false;
  }, [postAiCommand, searchExpanded, suppressAiEmbedPanel]);

  const closeMobileSearch = useCallback(() => {
    setHeaderSearch('');
    setSearchExpanded(false);
    searchInputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (!aiEnabled) {
      setAiAvailable(false);
      return;
    }

    let mounted = true;

    fetch(buildAiRequestUrl(aiApiBasePath, '/config'), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }
        return response.json();
      })
      .then((payload) => {
        if (!mounted) return;
        const data = ((payload as { data?: { enabled?: boolean } }).data ?? payload) as { enabled?: boolean };
        setAiAvailable(data.enabled !== false);
      })
      .catch(() => {
        if (mounted) setAiAvailable(true);
      });

    return () => {
      mounted = false;
    };
  }, [aiApiBasePath, aiEnabled]);

  const aiAssistantConfigRef = useRef(aiAssistantConfig);
  aiAssistantConfigRef.current = aiAssistantConfig;

  const postAiContext = useCallback(() => {
    const frameWindow = aiFrameRef.current?.contentWindow;
    if (!frameWindow) return;

    const cfg = aiAssistantConfigRef.current;
    const applicationCode = cfg?.applicationCode || getDefaultApplicationCode();
    const context = cfg?.contextProvider?.() || {};
    frameWindow.postMessage({
      type: 'qubepro-ai-context',
      payload: {
        applicationCode,
        pageContext: {
          path: location.pathname,
          search: location.search,
          title: document.title,
          ...(context.pageContext || {}),
        },
        userContext: context.userContext,
        companyContext: context.companyContext,
        uiContext: context.uiContext,
      },
    }, window.location.origin);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const timer = window.setTimeout(postAiContext, 100);
    return () => window.clearTimeout(timer);
  }, [postAiContext]);

  useEffect(() => {
    const handleAiMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'qubepro-ai-state') {
        if (suppressAiEmbedPanel) return;
        setAiOpen(Boolean(event.data?.payload?.open));
        if (typeof event.data?.payload?.expanded === 'boolean') {
          setAiExpanded(event.data.payload.expanded);
        }
      }
    };

    window.addEventListener('message', handleAiMessage);
    return () => window.removeEventListener('message', handleAiMessage);
  }, [suppressAiEmbedPanel]);

  const helpDocsUrl = helpConfig?.pageDocsUrl ?? null;
  const resolvedHelpDocsUrl = useMemo(() => (helpDocsUrl ? withLanguagePrefix(helpDocsUrl) : null), [helpDocsUrl, withLanguagePrefix]);
  const resolvedHelpBaseUrl = useMemo(
    () => (helpConfig?.docsBaseUrl ? withLanguagePrefix(helpConfig.docsBaseUrl) : null),
    [helpConfig?.docsBaseUrl, withLanguagePrefix],
  );
  const productTourLabel = productTourLaunchMode === 'start'
    ? helpConfig?.labels?.startTour
      ?? t.common?.productTour?.start
      ?? helpConfig?.labels?.restartTour
      ?? t.common?.productTour?.restart
    : productTourLaunchMode === 'restart'
      ? helpConfig?.labels?.restartTour ?? t.common?.productTour?.restart
      : null;


  useEffect(() => {
    if (notificationsBundleResetKey !== undefined) {
      resetTopHeaderNotificationBundleDedupeState();
    }
  }, [notificationsBundleResetKey]);

  useEffect(() => {
    if (!notificationOpen || !notificationsPollGate || !pageVisible) return;
    let cancelled = false;

    void dedupeNotificationBundle(() => resolvedProviderRef.current.load())
      .then((snapshot) => {
        if (cancelled) return;
        setNotificationItems(Array.isArray(snapshot.items) ? snapshot.items : []);
        setNotificationUnreadCount(Number(snapshot.unreadCount || 0));
        setNotificationPreferences({ ...(snapshot.preferences || {}) });
        notificationBroadcastRef.current?.postMessage({
          type: 'snapshot',
          source: notificationTabIdRef.current,
          snapshot: cloneNotificationSnapshot(snapshot),
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [notificationOpen, notificationsPollGate, pageVisible]);

  useEffect(() => {
    if (!notificationsPollGate || !pageVisible) return;

    let mounted = true;

    const loadInitialNotificationBadge = async () => {
      try {
        if (resolvedProviderRef.current.loadUnreadCount) {
          const unreadCount = Number(await resolvedProviderRef.current.loadUnreadCount()) || 0;
          if (!mounted) return;
          setNotificationUnreadCount(unreadCount);
          notificationBroadcastRef.current?.postMessage({
            type: 'snapshot',
            source: notificationTabIdRef.current,
            snapshot: cloneNotificationSnapshot({
              items: notificationItemsRef.current,
              unreadCount,
              preferences: notificationPreferencesRef.current,
            }),
          });
          return;
        }

        const snapshot = await dedupeNotificationHeaderBundle(() =>
          resolvedProviderRef.current.loadHeader
            ? resolvedProviderRef.current.loadHeader()
            : resolvedProviderRef.current.load(),
        );
        if (!mounted) return;
        setNotificationItems(Array.isArray(snapshot.items) ? snapshot.items : []);
        setNotificationUnreadCount(Number(snapshot.unreadCount || 0));
        if (Object.keys(snapshot.preferences || {}).length > 0) {
          setNotificationPreferences({ ...(snapshot.preferences || {}) });
        }
        notificationBroadcastRef.current?.postMessage({
          type: 'snapshot',
          source: notificationTabIdRef.current,
          snapshot: cloneNotificationSnapshot(snapshot),
        });
      } catch {
        // Keep the toolbar stable; the leader poll and dropdown load will retry.
      }
    };

    const initialTimer = window.setTimeout(() => void loadInitialNotificationBadge(), 80);

    return () => {
      mounted = false;
      window.clearTimeout(initialTimer);
    };
    // resolvedProvider ref uzerinden; kimlik dalgalanmasinda yeniden baglanmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolvedProviderRef ile guncel yukleyici okunur
  }, [notificationsPollGate, pageVisible, notificationsBundleResetKey]);

  useEffect(() => {
    if (!notificationsPollGate) {
      setNotificationItems([]);
      setNotificationUnreadCount(0);
      setNotificationPreferences({});
      return;
    }
    if (!pageVisible || !notificationPollLeader) {
      return;
    }

    let mounted = true;

    const loadNotifications = async (mode: 'header' | 'poll') => {
      try {
        if (!isCurrentNotificationLeader(notificationTabIdRef.current)) {
          return;
        }

        if (mode === 'poll' && resolvedProviderRef.current.loadUnreadCount) {
          const unreadCount = await resolvedProviderRef.current.loadUnreadCount();
          if (!mounted) return;
          setNotificationUnreadCount(unreadCount);
          notificationBroadcastRef.current?.postMessage({
            type: 'snapshot',
            source: notificationTabIdRef.current,
            snapshot: cloneNotificationSnapshot({
              items: notificationItemsRef.current,
              unreadCount,
              preferences: notificationPreferencesRef.current,
            }),
          });
          return;
        }

        const snapshot = await dedupeNotificationHeaderBundle(() =>
          resolvedProviderRef.current.loadHeader
            ? resolvedProviderRef.current.loadHeader()
            : resolvedProviderRef.current.load(),
        );
        if (!mounted) return;
        setNotificationItems(Array.isArray(snapshot.items) ? snapshot.items : []);
        setNotificationUnreadCount(Number(snapshot.unreadCount || 0));
        if (Object.keys(snapshot.preferences || {}).length > 0) {
          setNotificationPreferences({ ...(snapshot.preferences || {}) });
        }
        notificationBroadcastRef.current?.postMessage({
          type: 'snapshot',
          source: notificationTabIdRef.current,
          snapshot: cloneNotificationSnapshot(snapshot),
        });
      } catch {
        if (!mounted) return;
        setNotificationItems([]);
        setNotificationUnreadCount(0);
        setNotificationPreferences({});
      }
    };

    const initialTimer = window.setTimeout(() => void loadNotifications('header'), 120);
    const timer = window.setInterval(() => void loadNotifications('poll'), pollIntervalMs);

    return () => {
      mounted = false;
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
    };
    // resolvedProvider ref üzerinden; kimlik dalgalanmasında interval yeniden kurulmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolvedProviderRef ile güncel yükleyici okunur
  }, [notificationsPollGate, pageVisible, notificationPollLeader, pollIntervalMs]);

  const resolveTypeLabel = (type: string) => notificationTypeLabels[type] || type;
  const notificationCompanyNameById = useMemo(() => {
    const map = new Map<string, string>();
    notificationCompanies.forEach((company) => {
      const id = String(company.id ?? '').trim();
      const name = String(company.name ?? '').trim();
      if (id && name) map.set(id, name);
    });
    return map;
  }, [notificationCompanies]);
  const resolveNotificationCompanyLabel = useCallback(
    (item: TopHeaderToolbarNotificationItem) => {
      const companyName = String(item.companyName ?? '').trim();
      if (companyName) return companyName;

      const companyId = String(item.companyId ?? '').trim();
      if (!companyId) return '';
      return notificationCompanyNameById.get(companyId) || `Firma #${companyId}`;
    },
    [notificationCompanyNameById],
  );
  const resolveSourceAppMeta = useCallback(
    (sourceApp?: string | null) => {
      const sourceCode = normalizeSourceAppCode(sourceApp) || 'UNKNOWN';
      const registryCode = getRegistryCodeForSourceApp(sourceCode);
      const app = registryCode ? getApp(registryCode) : undefined;
      const label = pickLocalizedAppLabel(app, locale) || sourceCode;
      const iconColor = app ? getIconColor(app, theme) : 'var(--text-secondary)';
      return { sourceCode, app, label, iconColor };
    },
    [getApp, getIconColor, locale, theme],
  );

  return (
    <>
      <style>{TOOLBAR_CSS}</style>
      <form
        ref={searchRef}
        className={`qp-top-header-toolbar-search ${searchExpanded ? 'is-mobile-expanded' : ''}`}
        aria-expanded={searchExpanded}
        onClick={() => {
          openMobileSearch();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (openMobileSearch()) return;
          const value = (searchInputRef.current?.value ?? headerSearch).trim();
          if (!value) return;
          setHeaderSearch(value);
          const base = searchPath.split('?')[0] || searchPath;
          navigate(`${base}?q=${encodeURIComponent(value)}`);
        }}
      >
        <button
          type="submit"
          className="qp-top-header-toolbar-search-trigger"
          aria-label={searchAriaLabel}
          title={searchAriaLabel}
          onClick={(event) => {
            if (openMobileSearch()) {
              event.preventDefault();
            }
          }}
        >
          <Icon name="search" size={20} />
        </button>
        <input
          ref={searchInputRef}
          value={headerSearch}
          onChange={(event) => setHeaderSearch(event.target.value)}
          onFocus={() => setSearchExpanded(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              closeMobileSearch();
            }
          }}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
        />
        <button
          type="button"
          className="qp-top-header-toolbar-search-clear"
          aria-label={closeSearchAriaLabel}
          title={closeSearchAriaLabel}
          onClick={(event) => {
            event.stopPropagation();
            closeMobileSearch();
          }}
        >
          <Icon name="close" size={14} />
        </button>
      </form>

      <div className="qp-top-header-toolbar-notification-wrap" ref={notificationRef}>
        <button
          type="button"
          className="qp-top-header-toolbar-notification-btn"
          onClick={() => {
            setNotificationOpen((prev) => !prev);
            setHelpOpen(false);
            if (!suppressAiEmbedPanel) {
              postAiCommand('qubepro-ai-close');
              setAiOpen(false);
            }
          }}
          aria-label={notificationLabels?.title ?? t.common?.notificationsTitle ?? 'Notifications'}
        >
          <Icon name="bell" size={20} />
          {notificationUnreadCount > 0 ? (
            <span className="qp-top-header-toolbar-notification-badge">
              {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
            </span>
          ) : null}
        </button>

        {notificationOpen ? (
          <div className="qp-top-header-toolbar-notification-dropdown">
            <div className="qp-top-header-toolbar-notification-dropdown-head">
              <div>
                <div className="qp-top-header-toolbar-notification-title">{notificationLabels?.title ?? t.common?.notificationsTitle ?? 'Notifications'}</div>
                <div className="qp-top-header-toolbar-notification-subtitle">
                  {notificationUnreadCount} {notificationLabels?.unread ?? t.common?.notificationsUnread ?? 'unread'}
                  {mutedNotificationTypes.length > 0
                    ? ` · ${mutedNotificationTypes.length} ${notificationLabels?.typesMuted ?? t.common?.notificationsTypesMuted ?? 'types muted'}`
                    : ''}
                </div>
              </div>

              <div className="qp-top-header-toolbar-notification-head-actions">
                <button
                  type="button"
                  className="qp-top-header-toolbar-notification-link"
                  onClick={() => {
                    setNotificationOpen(false);
                    window.open(getNotificationCenterUrl(), '_blank', 'noopener,noreferrer');
                  }}
                >
                  {notificationLabels?.viewAll ?? t.common?.notificationsViewAll ?? 'View All'}
                </button>

                <button
                  type="button"
                  className="qp-top-header-toolbar-notification-link"
                  onClick={() => {
                    setNotificationOpen(false);
                    window.open(getNotificationCenterUrl('preferences'), '_blank', 'noopener,noreferrer');
                  }}
                >
                  {notificationLabels?.preferences ?? t.common?.notificationsPreferences ?? 'Preferences'}
                </button>
              </div>
            </div>

            {mutedNotificationTypes.length > 0 ? (
              <div className="qp-top-header-toolbar-notification-muted-summary">
                {notificationLabels?.mutedLabel ?? t.common?.notificationsMutedLabel ?? 'Muted'}: {mutedNotificationTypes.slice(0, 3).join(', ')}
                {mutedNotificationTypes.length > 3 ? ` +${mutedNotificationTypes.length - 3}` : ''}
              </div>
            ) : null}

            <div className="qp-top-header-toolbar-notification-list">
              {notificationItems.length === 0 ? (
                <div className="qp-top-header-toolbar-notification-empty">{notificationLabels?.empty ?? t.common?.notificationsEmpty ?? 'No notifications'}</div>
              ) : (
                notificationItems.map((item) => {
                  const appMeta = resolveSourceAppMeta(item.sourceApp);
                  const companyLabel = resolveNotificationCompanyLabel(item);
                  return (
                    <Fragment key={item.id}>
                    <button
                      type="button"
                      className={`qp-top-header-toolbar-notification-item ${item.isRead ? '' : 'unread'}`}
                      onClick={async () => {
                        try {
                          if (!item.isRead && resolvedProvider.markAsRead) {
                            await resolvedProvider.markAsRead(item);
                            setNotificationItems((prev) =>
                              prev.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)),
                            );
                            setNotificationUnreadCount((prev) => Math.max(0, prev - 1));
                          }
                        } catch {
                          // Sessiz devam et; yönlendirme engellenmesin.
                        }

                        setNotificationOpen(false);
                        // Üst panel özetinden tıklanınca her zaman merkezi Bildirim ürünü (yeni sekme);
                        // API `link` alanı bazı kayıtlarda yanlış rota (ör. /roles) içerebiliyor.
                        window.open(getNotificationCenterUrl(), '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <div className="qp-top-header-toolbar-notification-item-main">
                        <AppLogo
                          app={appMeta.app}
                          sourceCode={appMeta.sourceCode}
                          size={22}
                          color={appMeta.iconColor}
                          className="qp-top-header-toolbar-notification-app-logo"
                          title={appMeta.label}
                        />
                        <div className="qp-top-header-toolbar-notification-item-content">
                          <div className="qp-top-header-toolbar-notification-item-title">{item.title}</div>
                          <div className="qp-top-header-toolbar-notification-item-message">{item.message}</div>
                          <div className="qp-top-header-toolbar-notification-item-footer">
                            <div className="qp-top-header-toolbar-notification-item-tags">
                              {companyLabel ? (
                                <span
                                  className="qp-top-header-toolbar-notification-company-badge"
                                  title={companyLabel}
                                >
                                  {companyLabel}
                                </span>
                              ) : null}
                              <span className="qp-top-header-toolbar-notification-type-badge">
                                {resolveTypeLabel(item.type)}
                              </span>
                              {notificationPreferences[item.type] === false ? (
                                <span className="qp-top-header-toolbar-notification-muted-badge">
                                  {notificationLabels?.mutedLabel ?? t.common?.notificationsMutedLabel ?? 'Muted'}
                                </span>
                              ) : null}
                            </div>
                            <div className="qp-top-header-toolbar-notification-item-time">
                              {dateTime.formatDateTime(item.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    {sourceLinkFor(item) ? (
                      <button type="button" className="qp-top-header-toolbar-notification-link"
                        aria-label={locale.startsWith('tr') ? 'Kaynağa git' : 'Go to source'}
                        onClick={() => {
                          void loadAuthorizedNotificationRoutes().then((actions) => {
                            const href = sourceLinkFor(item, actions);
                            if (href) window.location.assign(href);
                          }).catch(() => { /* Yetki belirsizse fail-closed. */ });
                        }}>
                        {locale.startsWith('tr') ? 'Kaynağa git' : 'Go to source'}
                      </button>
                    ) : null}
                    </Fragment>
                  );
                })
              )}
            </div>

            {delegationsPath ? (
              <div className="qp-top-header-toolbar-notification-footer">
                <button
                  type="button"
                  className="qp-top-header-toolbar-notification-link"
                  onClick={() => {
                    setNotificationOpen(false);
                    navigate(delegationsPath);
                  }}
                >
                  {notificationLabels?.delegations ?? 'Delegations'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {helpConfig ? <HeaderHelpMenu
        config={{ ...helpConfig, docsBaseUrl: resolvedHelpBaseUrl || helpConfig.docsBaseUrl,
          pageDocsUrl: resolvedHelpDocsUrl || undefined }}
        platformBaseUrl={String(import.meta.env.VITE_BASE_URL || '')}
        language={locale} open={helpOpen} tourLabel={productTourLabel}
        onStartTour={() => { void productTourRef.current?.start(); }}
        onOpenChange={(open) => {
          setHelpOpen(open);
          if (open) {
            setNotificationOpen(false);
            if (!suppressAiEmbedPanel) { postAiCommand('qubepro-ai-close'); setAiOpen(false); }
          }
        }}
      /> : null}

      {aiEnabled && aiAvailable ? (
        <div className="qp-gva-container" ref={aiRef}>
          {suppressAiEmbedPanel ? (
            <button
              type="button"
              className={`qp-gva-trigger ${robotTriggerActive ? 'active' : ''}`}
              data-tour-id="ai-assistant"
              onClick={() => {
                setNotificationOpen(false);
                setHelpOpen(false);
                if (
                  typeof globalVoicePanelOpen === 'boolean' &&
                  aiOpen &&
                  !globalVoicePanelOpen
                ) {
                  onGlobalVoiceAssistantExpandPanel?.();
                  return;
                }
                const willOpen = !aiOpen;
                if (willOpen) onGlobalVoiceAssistantOpen?.();
                setAiOpen(willOpen);
              }}
              aria-label={aiLabels.button ?? 'QubePro AI Asistan'}
              title={aiLabels.button ?? 'QubePro AI Asistan'}
            >
              <Icon name="aiSpark" size={20} />
            </button>
          ) : (
            <>
              <button
                type="button"
                className={`qp-gva-trigger ${aiOpen ? 'active' : ''}`}
                data-tour-id="ai-assistant"
                onClick={() => {
                  setAiOpen((prev) => {
                    const next = !prev;
                    postAiCommand(next ? 'qubepro-ai-open' : 'qubepro-ai-close');
                    return next;
                  });
                  setNotificationOpen(false);
                  setHelpOpen(false);
                }}
                aria-label={aiLabels.button ?? 'QubePro AI Asistan'}
                title={aiLabels.button ?? 'QubePro AI Asistan'}
              >
                <Icon name="aiSpark" size={20} />
              </button>

              {(aiOpen || aiAssistantConfig?.initialOpen) ? (
                <div className={`qp-gva-panel ${aiOpen ? 'open' : ''} ${aiExpanded ? 'expanded' : ''}`}>
                  <iframe
                    ref={aiFrameRef}
                    className="qp-gva-frame"
                    src={aiEmbedUrl}
                    title={aiLabels.title ?? 'QubePro AI Asistan'}
                    onLoad={() => {
                      postAiContext();
                      if (aiOpen || aiAssistantConfig?.initialOpen) {
                        postAiCommand('qubepro-ai-open');
                      }
                    }}
                    allow="microphone; clipboard-read; clipboard-write; display-capture"
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      <ProductTourHost
        ref={productTourRef}
        onLaunchModeChange={setProductTourLaunchMode}
      />
    </>
  );
}
